import { adminUiCopy } from "@/copy/adminUiCopy";
import { formatDateTime } from "@/pages/docsShareLinks/docsShareLinksUtils";
import type {
  AdminDocsShareLink,
  DocsShareStatusFilter,
} from "@/types/docsShareLinks";
import {
  VNextButton,
  VNextCard,
  VNextNotice,
  VNextPill,
  VNextSelect,
} from "@/vnext/primitives";

interface DocsShareLinksTableProps {
  shareLinks: AdminDocsShareLink[];
  shareLinksErrorMessage: string | null;
  statusFilter: DocsShareStatusFilter;
  isShareLinksFetching: boolean;
  isCreatingShareLink: boolean;
  isRevokingShareLink: boolean;
  onStatusFilterChange: (value: DocsShareStatusFilter) => void;
  onRefreshShareLinks: () => Promise<void>;
  onRegenerateShareLink: (link: AdminDocsShareLink) => void;
  onRevokeShareLink: (link: AdminDocsShareLink) => void;
}

const getStatusTone = (
  status: AdminDocsShareLink["status"],
): "default" | "success" | "warning" => {
  if (status === "active") {
    return "success";
  }
  if (status === "expired") {
    return "warning";
  }
  return "default";
};

export function DocsShareLinksTable({
  shareLinks,
  shareLinksErrorMessage,
  statusFilter,
  isShareLinksFetching,
  isCreatingShareLink,
  isRevokingShareLink,
  onStatusFilterChange,
  onRefreshShareLinks,
  onRegenerateShareLink,
  onRevokeShareLink,
}: DocsShareLinksTableProps) {
  return (
    <VNextCard
      actions={
        <div className="docs-share-links-route__toolbar">
          <label className="docs-share-links-route__toolbar-field">
            <span>Status</span>
            <VNextSelect
              aria-label="Status"
              onChange={(event) =>
                onStatusFilterChange(
                  event.target.value as DocsShareStatusFilter,
                )
              }
              value={statusFilter}
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
              <option value="revoked">Revoked</option>
            </VNextSelect>
          </label>
          <VNextButton
            disabled={isShareLinksFetching}
            onClick={() => {
              void onRefreshShareLinks();
            }}
            title="Reload the current share-link list using the selected status filter."
            tone="secondary"
          >
            Refresh
          </VNextButton>
        </div>
      }
      subtitle="Review active, expired, and revoked links before regenerating or revoking access."
      title="Recent share links"
    >
      <VNextNotice tone="info">
        Status shows whether a link still works. Expiry blocks the link
        automatically. Accesses counts how many times the link has been opened.
      </VNextNotice>
      {shareLinksErrorMessage ? (
        <VNextNotice
          action={
            <VNextButton
              disabled={isShareLinksFetching}
              onClick={() => {
                void onRefreshShareLinks();
              }}
              tone="secondary"
            >
              Retry
            </VNextButton>
          }
          tone="danger"
        >
          {shareLinksErrorMessage}
        </VNextNotice>
      ) : null}

      {shareLinks.length === 0 && !isShareLinksFetching ? (
        <div className="vnext-empty">No share links found.</div>
      ) : (
        <div className="docs-share-links-route__table-shell">
          <table className="docs-share-links-route__table">
            <thead>
              <tr>
                <th>Doc path</th>
                <th>Status</th>
                <th>Expires</th>
                <th>Accesses</th>
                <th>Last access</th>
                <th>Created by</th>
                <th className="docs-share-links-route__table-actions">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {shareLinks.map((link) => (
                <tr key={link.id}>
                  <td>
                    <span className="docs-share-links-route__path">
                      {link.docPath}
                    </span>
                  </td>
                  <td>
                    <VNextPill tone={getStatusTone(link.status)}>
                      {link.status}
                    </VNextPill>
                  </td>
                  <td>{formatDateTime(link.expiresAt)}</td>
                  <td>{link.accessCount}</td>
                  <td>{formatDateTime(link.lastAccessedAt)}</td>
                  <td>
                    <span>{link.createdByLabel}</span>
                  </td>
                  <td className="docs-share-links-route__table-actions">
                    <div className="docs-share-links-route__row-actions">
                      <VNextButton
                        disabled={isCreatingShareLink}
                        onClick={() => {
                          onRegenerateShareLink(link);
                        }}
                        title={adminUiCopy.hoverHelp.docsShare.createNewLink}
                        tone="secondary"
                      >
                        Create new link
                      </VNextButton>
                      <VNextButton
                        disabled={
                          isRevokingShareLink || link.status === "revoked"
                        }
                        onClick={() => {
                          onRevokeShareLink(link);
                        }}
                        title={adminUiCopy.hoverHelp.docsShare.disableLink}
                        tone="quiet"
                      >
                        Disable link
                      </VNextButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </VNextCard>
  );
}
